import { record } from '../../studio/studio-content';
import { uiFormat } from '../../../i18n';
import { tailError } from '../../studio/circle/order-tail-model';
export interface TextSegment {
  speaker?: string;
  start_ms?: number;
  end_ms?: number;
  text: string;
}
export interface TextBlock {
  /** «Заголовок», «Описание», «Преимущества», «Ключевые слова» */
  label: string;
  text: string;
  /** Пункты преимуществ и ключевые слова — списком */
  items?: string[];
}
export interface TextDocument {
  blocks: TextBlock[];
  variants: string[];
  /** Подписи вариантов, только если их прислал сервер (03-api §9.4: variants[] {title, text}) */
  titles: string[];
  transcript: string;
  segments: TextSegment[];
  language?: string;
}
const value = (v: unknown) => (typeof v === 'string' && v.length <= 500000 ? v : '');
function strings(v: unknown, max = 200): string[] {
  return Array.isArray(v) && v.length <= max && v.every(item => typeof item === 'string' && item.length <= 20000) ? v : [];
}
/** Варианты: строки или {title, text}; любой другой вид — не варианты. */
function variantsOf(v: unknown): { texts: string[]; titles: string[] } {
  if (!Array.isArray(v) || v.length > 10) return { texts: [], titles: [] };
  const texts: string[] = [],
    titles: string[] = [];
  for (const item of v) {
    if (typeof item === 'string' && item.length <= 20000) {
      texts.push(item);
      titles.push('');
      continue;
    }
    const entry = record(item);
    const text = value(entry.text);
    if (!text || Array.from(text).length > 20000) return { texts: [], titles: [] };
    texts.push(text);
    titles.push(value(entry.title).slice(0, 80));
  }
  return { texts, titles };
}
/** Читаем реальные поля результата; неизвестный JSON не выдаём за готовый текст. */
export function parseTextResult(raw: unknown): TextDocument | null {
  try {
    if (JSON.stringify(raw).length > 512 * 1024) return null;
  } catch {
    return null;
  }
  const root = record(raw),
    result = record(root.result ?? root);
  const bullets = strings(result.bullets),
    keys = strings(result.keys ?? result.keywords);
  const blocks: TextBlock[] = [
    { label: 'Заголовок', text: value(result.title) },
    { label: 'Описание', text: value(result.desc ?? result.description) },
    { label: 'Преимущества', text: bullets.map(item => `• ${item}`).join('\n'), items: bullets },
    { label: 'Ключевые слова', text: keys.join(', '), items: keys },
  ].filter(v => v.text);
  const segments =
    Array.isArray(result.segments) && result.segments.length <= 5000
      ? result.segments.flatMap(v => {
          const item = record(v),
            text = value(item.text);
          if (!text) return [];
          return [
            {
              text,
              speaker:
                typeof item.speaker === 'number' && Number.isSafeInteger(item.speaker) && item.speaker >= 0
                  ? String(item.speaker + 1)
                  : value(item.speaker) || undefined,
              start_ms: typeof item.start_ms === 'number' && item.start_ms >= 0 ? item.start_ms : undefined,
              end_ms: typeof item.end_ms === 'number' && item.end_ms >= 0 ? item.end_ms : undefined,
            },
          ];
        })
      : [];
  const variants = variantsOf(result.variants);
  // Описание товара: блоки — главное; «text» рядом с блоками — не второй результат
  const document: TextDocument = {
    blocks,
    variants: variants.texts,
    titles: variants.titles,
    transcript: blocks.length ? '' : value(result.transcript) || value(result.text),
    segments,
    language: value(result.language) || undefined,
  };
  return blocks.length || document.variants.length || document.transcript || segments.length ? document : null;
}
export const speakerName = (speaker: string) => (/^\d+$/.test(speaker) ? uiFormat('Собеседник {0}', speaker) : speaker);
export function textDocumentPlain(value: TextDocument) {
  if (value.variants.length) return value.variants.join('\n\n');
  if (value.segments.length) return value.segments.map(s => `${s.speaker ? `${speakerName(s.speaker)}: ` : ''}${s.text}`).join('\n');
  return value.transcript || value.blocks.map(b => `${b.label}\n${b.text}`).join('\n\n');
}
/** Вход «Озвучки проектов» (scriptOf прототипа): реплики «Имя: текст», без собеседников — «Рассказчик: …». */
export function scriptOf(value: TextDocument): { text: string; speakers: string[] } {
  if (value.segments.some(s => s.speaker)) {
    const speakers = [...new Set(value.segments.map(s => (s.speaker ? speakerName(s.speaker) : 'Рассказчик')))];
    return { text: value.segments.map(s => `${s.speaker ? speakerName(s.speaker) : 'Рассказчик'}: ${s.text.trim()}`).join('\n'), speakers };
  }
  const lines = (value.segments.length ? value.segments.map(s => s.text) : textDocumentPlain(value).split('\n'))
    .map(line => line.replace(/^\s*\[?\d{1,2}:\d{2}(?::\d{2})?\]?\s*/, '').replace(/^•\s*/, '').trim())
    .filter(Boolean);
  return { text: lines.map(line => `Рассказчик: ${line}`).join('\n'), speakers: ['Рассказчик'] };
}
/** Отказы по входу, у которых есть свой человеческий текст: заголовок, подпись и главное действие хвоста.
    stt_no_speech — расшифровка прошла, но слов в записи нет: это не сбой сервиса, повтор с тем же файлом не поможет. */
const INPUT_FAILS: Record<string, { head: string; why: string; edit: string }> = {
  stt_no_speech: { head: 'В записи не нашлось речи', why: 'Загрузите файл, где слышно, как говорят.', edit: 'Заменить файл' },
};
export function textTailFail(order: Parameters<typeof tailError>[0]) {
  const code = tailError(order)?.code ?? '';
  return Object.prototype.hasOwnProperty.call(INPUT_FAILS, code) ? INPUT_FAILS[code] : null;
}
